<script setup lang="ts">
import { computed } from 'vue';
import { TaskStatus } from '@/api';
import { demoAreaLabel, useDemoSeedStore } from '@/stores/demoSeedStore';
import { taskStatusMap } from '@/components/design/statusMaps';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import GaiaTaskPanel from '@/components/design/composite/GaiaTaskPanel.vue';
import GaiaTaskRow from '@/components/design/composite/GaiaTaskRow.vue';
import type { GaiaIndicatorStatus } from '@/components/design/base/GaiaStatusIndicator.vue';

const demoSeed = useDemoSeedStore();

const progressPercentage = computed<number | undefined>(() => {
  const job = demoSeed.job;
  if (!job || job.status !== TaskStatus.RUNNING) return undefined;
  if (job.areas.length === 0) return 0;
  return Math.min(100, Math.round((job.completed_areas.length / job.areas.length) * 100));
});

// The currently-running step: the first area not yet completed.
const currentStepLabel = computed(() => {
  const job = demoSeed.job;
  if (!job) return '';
  const current = job.areas.at(job.completed_areas.length);
  return current ? demoAreaLabel(current) : '';
});

const headerLabel = computed(() => {
  if (!demoSeed.isRunning) {
    return 'Demo data';
  }
  return demoSeed.mode === 'remove' ? 'Removing demo data…' : 'Seeding demo data…';
});

const rowStatus = computed<GaiaIndicatorStatus>(() =>
  demoSeed.job ? taskStatusMap[demoSeed.job.status].status : 'pending'
);

const rowLabel = computed(() => {
  const job = demoSeed.job;
  if (!job) return '';
  if (job.status === TaskStatus.RUNNING) return `${currentStepLabel.value}…`;
  if (job.status === TaskStatus.DONE) {
    return demoSeed.mode === 'remove' ? 'Demo data removed' : 'Demo data completed';
  }
  return demoSeed.mode === 'remove' ? 'Removing demo data failed' : 'Seeding demo data failed';
});

const rowDetail = computed(() => {
  const job = demoSeed.job;
  if (!job) return undefined;
  if (job.status === TaskStatus.RUNNING) {
    return `${job.completed_areas.length} of ${job.areas.length} areas`;
  }
  if (job.status === TaskStatus.DONE) return undefined;
  return job.error ?? 'The demo data operation failed.';
});
</script>

<template>
  <gaia-task-panel v-if="demoSeed.job" :title="headerLabel" :collapsible="false">
    <template #header-extra>
      <gaia-tooltip>
        <template #trigger>
          <gaia-icon-button :dimension="24" quaternary @click="demoSeed.dismiss()">
            <svg
              width="11"
              height="11"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
            >
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </gaia-icon-button>
        </template>
        Dismiss
      </gaia-tooltip>
    </template>

    <gaia-task-row
      :status="rowStatus"
      :label="rowLabel"
      :detail="rowDetail"
      :percentage="progressPercentage"
    />
  </gaia-task-panel>
</template>
